/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-12
 */

// Add third-party dependencies.
import {
  Card,
  CardContent,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from '@mui/material';
import {
  Business,
  PersonAdd,
  GroupAdd,
  AdminPanelSettings,
  Group,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// Add local dependencies.
import type { DashboardOverviewResponse } from '../../../../types/dashboard';
import {
  calculateStudioOwnershipMetrics,
  formatStudioRole,
  formatActivityTimestamp,
} from '../../../../utils/dashboardHelpers';

interface RecentStudioActivityCardProps {
  dashboardData?: DashboardOverviewResponse;
}

/**
 * Recent studio activity card component for the dashboard.
 * Displays recent member joins, role changes, and studio updates for owned studios.
 *
 * @example
 * return (
 *   <RecentStudioActivityCard dashboardData={dashboardData} />
 * );
 *
 * @returns {React.ReactElement} The recent studio activity card component
 */
const RecentStudioActivityCard: React.FC<RecentStudioActivityCardProps> = ({
  dashboardData,
}) => {
  const { t } = useTranslation('dashboard');
  const studios = dashboardData?.data?.studios || [];
  const recentActivity = dashboardData?.data?.recent_activity || [];

  // Get ownership metrics
  const ownershipMetrics = calculateStudioOwnershipMetrics(studios);

  // Filter activities related to studio management
  const studioActivities = recentActivity
    .filter(
      activity =>
        activity.type === 'studio_joined' ||
        activity.type === 'invitation_sent' ||
        activity.type === 'invitation_accepted'
    )
    .slice(0, 5); // Show last 5 studio-related activities

  // Get activity icon based on type
  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'studio_joined':
        return <GroupAdd color="success" />;
      case 'invitation_sent':
        return <PersonAdd color="warning" />;
      case 'invitation_accepted':
        return <Group color="primary" />;
      default:
        return <Business color="secondary" />;
    }
  };

  // Get activity priority color
  const getActivityColor = (
    type: string
  ): 'success' | 'warning' | 'primary' | 'default' => {
    switch (type) {
      case 'studio_joined':
        return 'success';
      case 'invitation_sent':
        return 'warning';
      case 'invitation_accepted':
        return 'primary';
      default:
        return 'default';
    }
  };

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <AdminPanelSettings sx={{ color: 'primary.main', mr: 1 }} />
          <Typography variant="h6" component="h2" sx={{ flexGrow: 1 }}>
            {t('studioManagement.title')}
          </Typography>
          {ownershipMetrics.ownedStudios > 0 && (
            <Chip
              label={
                ownershipMetrics.ownedStudios > 1
                  ? t('studioManagement.studiosLabel_plural', {
                      count: ownershipMetrics.ownedStudios,
                    })
                  : t('studioManagement.studiosLabel', {
                      count: ownershipMetrics.ownedStudios,
                    })
              }
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {ownershipMetrics.ownedStudios === 0
            ? t('studioManagement.description.noStudios')
            : t('studioManagement.description.hasStudios')}
        </Typography>

        {ownershipMetrics.ownedStudios > 0 ? (
          <>
            {/* Studio Ownership Summary */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('studioManagement.yourStudios')}
              </Typography>
              {ownershipMetrics.ownedStudiosList.map(studio => (
                <Box
                  key={studio.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1,
                    '&:last-child': { mb: 0 },
                  }}
                >
                  <Business sx={{ fontSize: 16, color: 'primary.main' }} />
                  <Typography variant="body2" sx={{ flexGrow: 1 }}>
                    {studio.name}
                  </Typography>
                  <Chip
                    label={formatStudioRole(studio.role)}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Box>
              ))}
            </Box>

            {/* Recent Studio Activities */}
            {studioActivities.length > 0 ? (
              <List sx={{ p: 0 }}>
                {studioActivities.map(activity => (
                  <ListItem
                    key={`${activity.type}-${activity.timestamp}`}
                    sx={{
                      px: 0,
                      py: 1,
                      borderBottom: 1,
                      borderColor: 'divider',
                      '&:last-child': {
                        borderBottom: 0,
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {getActivityIcon(activity.type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          <Typography variant="subtitle2">
                            {activity.description}
                          </Typography>
                          <Chip
                            label={activity.type.replace('_', ' ')}
                            size="small"
                            color={getActivityColor(activity.type)}
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          component="span"
                        >
                          {formatActivityTimestamp(activity.timestamp)}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 3,
                  textAlign: 'center',
                }}
              >
                <Group sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  {t('studioManagement.noMemberActivity')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('studioManagement.noMemberActivityDescription')}
                </Typography>
              </Box>
            )}
          </>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 4,
              textAlign: 'center',
            }}
          >
            <Business sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              {t('studioManagement.noStudiosOwned')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('studioManagement.noStudiosOwnedDescription')}
            </Typography>
          </Box>
        )}

        <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary" align="center">
            {t('studioManagement.comingSoon')}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default RecentStudioActivityCard;
